import { useCallback, useEffect, useMemo, useState } from "react";
import {
  IconArrowLeft,
  IconCheck,
  IconCircle,
  IconCirclePlus,
  IconEqual,
  IconInfoCircle,
} from "@tabler/icons-react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { brazilStates } from "~/constants";
import {
  ComissionCard,
  Form,
  Input,
  PageLoader,
  Radio,
  Select,
} from "~/components";
import { Option, personTypeOptions } from "~/utils/options";
import { usePartner } from "~/contexts/partner";
import { documentSchema, phoneSchema } from "~/utils/yupSchema";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { clearString, transformMoney } from "~/utils";
import { useTheme } from "@emotion/react";
import { useDealershipsPartner, useIndicationFlow } from "~/contexts";
import { BasicDataPendency, hasPendencyOrNullOnBasicData } from "../utils";
import {
  EIndicationStatus,
  EPersonType,
  UpdateIndicationDealershipLoginDto,
  useGetProposalAndIndicationByIdLazyQuery,
  useUpdateIndicationMutation,
  useDealershipListQuery,
} from "~/graphql/types";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { ComissionType } from "~/components/ComissionCard";

type FormData = {
  name: string;
  document: string;
  email: string;
  phone: string;
  uf: string;
  dealershipId: string;
  averageEnergyBillValueCents: number;
  commissionPercentage: number;
  personType: EPersonType;
  dealershipLogin: UpdateIndicationDealershipLoginDto;
};

export const BasicData: React.FC = () => {
  const theme = useTheme();
  const isLowerSm = useMediaQuery(theme.breakpoints.down("sm"));
  const { myPartner } = usePartner();
  const { dealerships, dealershipsIsLoading } = useDealershipsPartner();
  const {
    selectedIndication,
    selectedCustomer,
    setSelectedIndication,
    selectedContract,
    setSelectedContract,
  } = useIndicationFlow();

  const { data: dealershipListData, loading: dealershipListLoading } =
    useDealershipListQuery({ variables: { params: { pageSize: 99999 } } });

  const schema = yup
    .object({
      name: yup.string().required(),
      document: documentSchema,
      email: yup.string().email().required(),
      phone: phoneSchema,
    })
    .required();

  const formMethods = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      uf: "",
      commissionPercentage: 0,
    },
  });

  const watchFields = useWatch({ control: formMethods.control });
  const isPf = watchFields.personType === EPersonType.pf;
  const selectedDealership = dealerships.find(
    (item) => item.dealershipId === watchFields.dealershipId,
  );
  const [consistencyWaiting, setConsistencyWaiting] = useState(false);

  const [
    getProposalAndIndicationById,
    { loading: getProposalAndIndicationByIdIsLoading },
  ] = useGetProposalAndIndicationByIdLazyQuery({
    onCompleted(data) {
      if (
        data.getIndicationRelatedEntitiesById.contract?._id &&
        data.getIndicationById._id
      ) {
        setSelectedIndication(data.getIndicationById);
        setSelectedContract(data.getIndicationRelatedEntitiesById.contract);
        toast.success("Os dados básicos foram alterados!");
      }
    },
  });

  const [updateIndication, { loading: updateIndicationIsLoading }] =
    useUpdateIndicationMutation();

  useEffect(() => {
    if (selectedContract) {
      formMethods.setValue(
        "averageEnergyBillValueCents",
        transformMoney(
          selectedIndication?.averageEnergyBillValueCents || 0,
          "toReal",
        ),
      );
      formMethods.setValue(
        "uf",
        selectedContract.indicationRef?.condition?.uf || "",
      );
      formMethods.setValue(
        "dealershipId",
        selectedContract?.energyBillInfo?.dealershipId || "",
      );
      formMethods.setValue("document", selectedContract.customerRef.document);
      formMethods.setValue("email", selectedContract.customerRef.email || "");
      formMethods.setValue("name", selectedContract?.customerRef.name || "");
      formMethods.setValue("phone", selectedContract.customerRef.phone || "");
      if (selectedContract?.dealershipLogin) {
        formMethods.setValue(
          "dealershipLogin.login",
          selectedContract.dealershipLogin.login,
        );
        formMethods.setValue(
          "dealershipLogin.password",
          selectedContract.dealershipLogin.password,
        );
        formMethods.setValue(
          "dealershipLogin.extraPassword",
          selectedContract.dealershipLogin.extraPassword,
        );
      }
      if (selectedContract?.customerRef.personType) {
        formMethods.setValue(
          "personType",
          selectedContract?.customerRef.personType as EPersonType,
        );
      }
      if (selectedIndication?.condition?.customerDiscountOffered) {
        formMethods.setValue(
          "commissionPercentage",
          selectedIndication?.condition?.customerDiscountOffered,
        );
      }

      const fieldsToSetError: BasicDataPendency[] = [
        "name",
        "document",
        "email",
        "phone",
      ];

      fieldsToSetError.map((item) => {
        if (
          selectedContract?.customerRef?.pendencies?.[
            item as keyof typeof selectedContract.customerRef.pendencies
          ]?.rejectedAt
        ) {
          formMethods.setError(item as any, {
            message: "Este campo foi reprovado por um analista!",
          });
        }
      });
    }
  }, [selectedContract, selectedIndication, selectedCustomer]);

  const dealershipOptions: Option[] = useMemo(
    () =>
      dealershipListData?.dealershipList?.data?.map((item) => {
        return { value: item._id, label: item.name || "" };
      }) || [],
    [dealershipListData],
  );

  // Referência única aos campos sujos para reutilizar em enableSubmit e onSubmit
  const { dirtyFields } = formMethods.formState;

  const onSubmit = useCallback(
    async (data: FormData) => {
      // const hasDealershipLogin = [data?.dealershipLogin?.login, data?.dealershipLogin?.password, data?.dealershipLogin?.extraPassword].some(item => item)

      await updateIndication({
        variables: {
          params: {
            id: selectedIndication?._id || "",
            email: data.email,
            name: data.name,
            phone: clearString(data.phone),
            uf: data.uf,
            document: clearString(data.document),
            dealerShipName:
              dealershipListData?.dealershipList?.data?.find(
                (d) => d._id === data.dealershipId,
              )?.name || null,
            averageEnergyBillValueCents: transformMoney(
              Number(data.averageEnergyBillValueCents || 0),
              "toCents",
            ),

            // ...isPf && {
            //   maritalStatus: data.maritalStatus,
            //   nationality: data.nationality,
            // },
            // dealershipLogin: hasDealershipLogin ? data.dealershipLogin : null
          },
        },
      });

      // Aguarda 5s para consistência do backend antes de refazer a consulta
      setConsistencyWaiting(true);
      await new Promise((resolve) => setTimeout(resolve, 5000));
      await getProposalAndIndicationById({
        variables: { id: selectedIndication?._id || "" },
      });
      setConsistencyWaiting(false);
    },
    [updateIndication, selectedIndication, isPf],
  );

  const filtersStates = useMemo(() => brazilStates, []);

  const discountMax = isPf
    ? selectedDealership?.customerPFDiscountMax
    : selectedDealership?.customerPJDiscountMax;
  const discountMin = isPf
    ? selectedDealership?.customerPFDiscountMin
    : selectedDealership?.customerPJDiscountMin;

  const hasCommissionPercentage = Boolean(watchFields.commissionPercentage);

  // const roofPercentage = (discountMax || 0) + (myPartner?.partnershipConditions?.baseCommissionPercentage || 0)

  // const baseComission = selectedIndication?.condition?.baseCommissionPercentage ? selectedIndication?.condition?.baseCommissionPercentage * 100 : 0
  // const aditionalComission = selectedIndication?.condition?.additionalCommissionPercentage ? selectedIndication?.condition?.additionalCommissionPercentage * 100 : 0

  // const finalComission = selectedIndication?.condition?.commissionAmountPercentage ? selectedIndication?.condition?.commissionAmountPercentage * 100 : 0

  const indicationCondition = selectedIndication?.condition;

  const allowedStatusToUpdate = [
    EIndicationStatus.requestedProposal,
    EIndicationStatus.contractWithPendencies,
  ];
  const isStatusAllowed = allowedStatusToUpdate.includes(
    selectedIndication?.status as EIndicationStatus,
  );

  // Como apenas campos com pendência ficam habilitados para edição,
  // basta checar se algum desses campos está sujo (dirty) para habilitar o submit.
  const enableSubmit = isStatusAllowed && Boolean(
    dirtyFields?.name ||
    dirtyFields?.email ||
    dirtyFields?.phone ||
    dirtyFields?.document ||
    dirtyFields?.uf ||
    dirtyFields?.averageEnergyBillValueCents ||
    dirtyFields?.dealershipId,
  );

  if (dealershipListLoading) return <PageLoader />;

  return (
    <>
      {/* <IndicationsCreateBanner /> */}
      <FormProvider {...formMethods}>
        <Form onSubmit={formMethods.handleSubmit((data) => onSubmit(data))}>
          <Box
            sx={{ display: "flex", flex: 1, flexDirection: "column", gap: 3 }}
          >
            <Typography
              variant="h3"
              fontWeight={500}
              color={theme.palette.grey[800]}
            >
              Tipo de pessoa:
            </Typography>

            <Radio
              disabled={true}
              row
              name="personType"
              options={personTypeOptions}
            />

            <Typography
              variant="h3"
              fontWeight={500}
              color={theme.palette.grey[800]}
            >
              Dados
            </Typography>
            <Grid spacing={3} container>
              <Grid item md={8} xs={12}>
                <Input
                  disabled={hasPendencyOrNullOnBasicData(
                    "name",
                    selectedContract?.customerRef.pendencies,
                  )}
                  name="name"
                  label="Nome completo"
                />
              </Grid>

              <Grid item md={4} xs={12}>
                <Input
                  disabled={true}
                  name="document"
                  label={isPf ? "CPF" : "CNPJ"}
                  mask={isPf ? "cpf" : "cnpj"}
                />
              </Grid>

              <Grid item md={4} xs={12}>
                <Input
                  disabled={hasPendencyOrNullOnBasicData(
                    "email",
                    selectedContract?.customerRef.pendencies,
                  )}
                  name="email"
                  label="E-mail"
                />
              </Grid>
              <Grid item md={4} xs={12}>
                <Input
                  disabled={hasPendencyOrNullOnBasicData(
                    "phone",
                    selectedContract?.customerRef.pendencies,
                  )}
                  name="phone"
                  label="Telefone"
                  mask="phone"
                />
              </Grid>
              <Grid item md={4} xs={12}>
                <Select
                  disabled={true}
                  variant="outlined"
                  name="uf"
                  label="UF"
                  options={filtersStates}
                />
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography
                  variant="h3"
                  fontWeight={500}
                  color={theme.palette.grey[800]}
                >
                  Chaves de acesso da concessionária
                </Typography>
              </Grid>
              <Grid item lg={4} md={6} sm={12} xs={12}>
                <Input
                  disabled={true}
                  label="Login"
                  name="dealershipLogin.login"
                />
              </Grid>
              <Grid item lg={4} md={6} sm={12} xs={12}>
                <Input
                  disabled={true}
                  type="password"
                  label="Senha"
                  name="dealershipLogin.password"
                />
              </Grid>
              <Grid item lg={4} md={6} sm={12} xs={12}>
                <Input
                  disabled={true}
                  type="password"
                  label="Chave extra"
                  name="dealershipLogin.extraPassword"
                />
              </Grid>
            </Grid>

            <Grid container>
              <Grid item xs={12}>
                <Typography
                  sx={{ padding: "0 0 1rem 0" }}
                  variant="h3"
                  fontWeight={500}
                  color={theme.palette.grey[800]}
                >
                  Percentual de economia
                </Typography>
              </Grid>

              {/* <Grid item xs={12}>
                {Boolean(selectedDealership) && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '.4rem', padding: '0 0 1rem 0' }}>
                    <IconInfoCircle color={theme.palette.info.dark} />
                    <Box>
                      <Typography sx={{ fontWeight: 500 }} variant='body1' color={theme.palette.grey[900]}>
                        O valor deve estar entre {discountMin}% e {roofPercentage}%* para a concessionária informada
                      </Typography>
                      <Typography variant='body1' color={theme.palette.grey[600]}>
                        *em que {discountMax}% são do valor máximo de desconto para o cliente e {myPartner?.partnershipConditions?.baseCommissionPercentage}% são substraídos da sua comissão base.
                      </Typography> 
                    </Box>
                  </Box>
                )}
              </Grid> */}

              <Grid item md={4} xs={12}>
                <Input
                  disabled={true}
                  name="commissionPercentage"
                  label="Percentual de economia"
                  mask="percentage"
                />
              </Grid>
            </Grid>

            {Boolean(
              indicationCondition?.anticipated?.baseCommissionPercentage,
            ) && (
              <ComissionCard
                additionalCommissionPercentage={
                  indicationCondition?.anticipated
                    ?.additionalCommissionPercentage || 0
                }
                baseCommissionPercentage={
                  indicationCondition?.anticipated?.baseCommissionPercentage ||
                  0
                }
                commissionAmountPercentage={
                  indicationCondition?.anticipated
                    ?.commissionAmountPercentage || 0
                }
                hasComissionSelected={hasCommissionPercentage}
                typeComission={ComissionType.initial}
              />
            )}

            {Boolean(
              indicationCondition?.recurring?.baseCommissionPercentage,
            ) && (
              <ComissionCard
                additionalCommissionPercentage={
                  indicationCondition?.recurring
                    ?.additionalCommissionPercentage || 0
                }
                baseCommissionPercentage={
                  indicationCondition?.recurring?.baseCommissionPercentage || 0
                }
                commissionAmountPercentage={
                  indicationCondition?.recurring?.commissionAmountPercentage ||
                  0
                }
                hasComissionSelected={hasCommissionPercentage}
                typeComission={ComissionType.recurring}
              />
            )}

            {/* <Box sx={{ background: '#fafafa', border: `1px solid ${theme.palette.grey[300]}`, padding: '1.4rem', borderRadius: '.8rem' }}>
              <Typography sx={{ paddingBottom: '1rem' }} variant='h3' fontWeight={500} color={theme.palette.grey[800]}>Sua comissão</Typography>
              <Box sx={{ display: 'flex', gap: isLowerSm ? '1rem' : '2rem', alignItems: isLowerSm ? 'flex-start' : 'center', flexFlow: isLowerSm ? 'column' : 'row' }}>
                <Box sx={{ display: 'flex', flexFlow: 'column', gap: '.2rem' }}>
                  <Typography variant='body2' color={theme.palette.grey[600]}>Comissão base</Typography>
                  <Typography variant='h3' fontWeight={500} color={theme.palette.grey[800]}>{Number(baseComission.toFixed(2))}%</Typography>
                </Box>
                <IconCirclePlus color={theme.palette.secondary.main} size={18} />
                <Box sx={{ display: 'flex', flexFlow: 'column', gap: '.2rem' }}>
                  <Typography variant='body2' color={theme.palette.grey[600]}>Comissão adicional</Typography>
                  <Typography variant='h3' fontWeight={500} color={hasCommissionPercentage ? theme.palette.grey[800] : theme.palette.grey[300]}>{aditionalComission}%</Typography>
                </Box>
                <Box sx={{ position: 'relative' }}>
                  <IconCircle color={theme.palette.secondary.main} size={18} />
                  <IconEqual color={theme.palette.secondary.main} size={14} style={{ position: 'absolute', top: '2px', left: '2px' }} />
                </Box>
                <Box sx={{ display: 'flex', flexFlow: 'column', gap: '.2rem' }}>
                  <Typography variant='body2' color={theme.palette.grey[600]}>Comissão final</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '.6rem' }}>
                    <IconCheck color={hasCommissionPercentage ? theme.palette.success.main : theme.palette.grey[300]} />
                    <Typography variant='h3' fontWeight={500} color={hasCommissionPercentage ? theme.palette.grey[800] : theme.palette.grey[300]}>{Number(finalComission.toFixed(2))}%</Typography>
                  </Box>
                </Box>
              </Box>
            </Box> */}

            <Divider />

            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Link to="/app/indications">
                <Button startIcon={<IconArrowLeft />} color="secondary">
                  Voltar
                </Button>
              </Link>

              <Button
                disabled={
                  !enableSubmit ||
                  updateIndicationIsLoading ||
                  getProposalAndIndicationByIdIsLoading ||
                  consistencyWaiting
                }
                type="submit"
                startIcon={
                  updateIndicationIsLoading ||
                  getProposalAndIndicationByIdIsLoading ||
                  consistencyWaiting ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    <IconCheck size={24} />
                  )
                }
              >
                Confirmar
              </Button>
            </Box>
          </Box>
        </Form>
      </FormProvider>
    </>
  );
};
